/*
 * Pure PII-scrub logic extracted from resumeSanitizer.js so it can be
 * unit-tested from Node/Jest without pulling in pdfjs-dist or mammoth.
 *
 * `scrubText(input) -> string` is the entry point. Everything here is a
 * pure function of its input — no I/O, no worker, no DOM.
 */

// Section headings that mark the end of the résumé header. Case-insensitive.
// Covers EN, PT-BR, ES-419 (Latin American Spanish), and FR-CA (Canadian
// French). Localized keywords use accented characters as they appear in
// real résumés; matching itself is accent-preserving (résumé authors keep
// accents so we rely on them for high-signal matches — HEADING_RE below is
// applied to the raw line, no fold-to-ASCII pass is performed).
const SECTION_HEADING_PATTERNS = [
  // English
  "professional experience", "work experience", "employment history",
  "employment", "experience", "professional summary", "career summary",
  "summary", "profile", "objective", "career objective",
  "skills", "technical skills", "core skills", "core competencies", "competencies",
  "education", "academic background", "qualifications", "academic",
  "certifications", "licenses", "licences", "certificates",
  "projects", "key projects", "selected projects",
  "achievements", "key achievements", "awards", "honors", "honours",
  "languages", "publications", "volunteer", "volunteering",
  // Portuguese
  "experiência profissional", "experiência", "histórico profissional",
  "resumo profissional", "perfil", "objetivo", "objetivo profissional",
  "competências", "competências técnicas", "habilidades",
  "formação", "formação acadêmica", "educação", "qualificações",
  "certificações", "certificados", "licenças",
  "projetos", "principais projetos",
  "conquistas", "prêmios", "reconhecimentos",
  "idiomas", "publicações", "voluntariado",
  // Spanish (Latin American)
  "experiencia profesional", "experiencia laboral", "historial laboral",
  "trayectoria profesional", "experiencia",
  "resumen profesional", "resumen ejecutivo", "resumen",
  "perfil profesional", "objetivo profesional",
  "habilidades", "habilidades técnicas", "competencias principales",
  "competencias", "aptitudes", "conocimientos",
  "formación académica", "formación", "educación", "estudios",
  "titulación", "titulaciones",
  "certificaciones", "certificaciones y capacitación",
  "logros", "logros clave", "premios", "reconocimientos", "distinciones",
  "idiomas", "publicaciones", "voluntariado",
  "sistemas y herramientas", "herramientas",
  // French (Canadian)
  "expérience professionnelle", "expériences professionnelles",
  "expérience", "expériences", "parcours professionnel", "historique professionnel",
  "résumé professionnel", "résumé", "profil professionnel", "profil",
  "objectif", "objectif professionnel", "objectif de carrière",
  "compétences", "compétences principales", "compétences techniques",
  "aptitudes", "savoir-faire", "connaissances",
  "formation", "formation académique", "éducation", "scolarité", "études",
  "diplômes", "qualifications",
  "certifications", "certifications et formation", "attestations",
  "projets", "projets clés", "projets principaux",
  "réalisations", "prix", "distinctions", "récompenses", "honneurs",
  "langues", "publications", "bénévolat",
  "systèmes et outils", "outils",
];

export const HEADING_RE = new RegExp(
  "^\\s*(" +
    SECTION_HEADING_PATTERNS.map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|") +
    ")\\s*[:\\-–—]?\\s*$",
  "i"
);

// References/letter-of-recommendation section start — everything from the
// heading to end-of-document is removed. Covers EN, PT-BR, ES-419, FR-CA.
export const REFERENCES_RE = /^\s*(references|referências|referencias|références|référencias|professional references|referências profissionais|referencias profesionales|références professionnelles|letters? of recommendation|letters? of reference|carta de recomendação|cartas de recomendação|carta de referência|cartas de referência|cartas de recomendación|cartas de referencia|lettres? de recommandation|lettres? de référence)\s*[:\-–—]?\s*$/i;

// PII regexes ---------------------------------------------------------------

export const EMAIL_RE = /[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}/gi;

export const URL_RE = /(?:https?:\/\/|www\.)\S+/gi;
export const SOCIAL_HANDLE_RE = /\b(linkedin\.com|github\.com|gitlab\.com|behance\.net|dribbble\.com|medium\.com|twitter\.com|x\.com|facebook\.com|instagram\.com|tiktok\.com|youtube\.com|t\.me|telegram\.me|wa\.me)\/\S+/gi;

// Phones — Brazilian mobile/landline + international +CC formats.
// Includes phone-line keywords in EN, PT-BR, ES-419, FR-CA.
export const PHONE_KEYWORDS_RE = /(?:phone|tel|telefone|teléfono|téléphone|mobile|móvil|movil|celular|cellulaire|portable|whatsapp|whats)\s*[:.\-]?\s*([+\d][\d\s().\-]{7,}\d)/gi;
export const RAW_PHONE_RE = /(\+?\d[\d\s().\-]{8,}\d)/g;

// Government IDs — Brazilian and international patterns.
export const CPF_RE = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g;
export const CNPJ_RE = /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g;
export const RG_RE = /\b\d{1,2}\.?\d{3}\.?\d{3}-?\d{1,2}\b/g;
export const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g;
export const SIN_RE = /\b\d{3}[\s\-]?\d{3}[\s\-]?\d{3}\b/g;
export const PASSPORT_RE = /(?:passport|passaporte|pasaporte|passeport)\s*(?:no\.?|number|nº|n°|:)?\s*[A-Z0-9]{6,10}/gi;

// Date of birth patterns. Covers EN, PT-BR, ES-419, FR-CA.
export const DOB_KEYWORDS_RE = /\b(date of birth|dob|birthdate|born on|nascimento|data de nascimento|nascido em|nascida em|fecha de nacimiento|fecha de nac\.?|nacido el|nacida el|date de naissance|né(?:\(?e\)?)? le|née le)\b[:\-\s]*[^\n]{0,40}/gi;

// Contact-label lines (Name:, Address:, Marital status:, …).
// NOTE: no `g` flag — `.test()` is called per-line and the global flag
// makes RegExp state persist between calls, producing false negatives.
// Covers EN, PT-BR, ES-419, FR-CA.
export const CONTACT_LABEL_RE = /^\s*(name|nombre|nome|nom|prénom|address|dirección|endereço|adresse|residência|residence|city|ciudad|cidade|ville|state|estado|province|zip|cep|postal code|code postal|country|país|pays|nationality|nacionalidad|nacionalidade|nationalité|gender|género|sexo|sexe|genre|marital|estado civil|état civil|date of birth|fecha de nacimiento|data de nascimento|date de naissance|email|correo|correo electrónico|courriel|e-mail|phone|teléfono|telefone|téléphone|mobile|móvil|celular|cellulaire|portable|linkedin|linked ?in)\s*[:\-–—]\s*.*$/im;

/**
 * Strip PII from raw résumé text.
 *
 * Steps:
 *   1. Split into lines and drop everything before the first recognised
 *      section heading (résumé header block: name/email/phone/address/photo
 *      references).
 *   2. Detect a "References" or "Letters of Recommendation" heading and
 *      drop everything from that point on.
 *   3. Walk the surviving lines and blank out any direct identifier via
 *      regex so identifiers outside the header (footers, prose) are
 *      removed too.
 *
 * @param {string} input
 * @returns {string}
 */
export function scrubText(input) {
  const rawLines = String(input || "").split(/\r?\n/);

  // Step 1: strip header until first section heading.
  const firstSectionIdx = rawLines.findIndex((ln) => HEADING_RE.test(ln.trim()));
  const startIdx = firstSectionIdx >= 0 ? firstSectionIdx : 0;

  // Step 2: cut off References section.
  const refsRelative = rawLines
    .slice(startIdx)
    .findIndex((ln) => REFERENCES_RE.test(ln.trim()));
  const endIdx = refsRelative >= 0 ? startIdx + refsRelative : rawLines.length;

  const bodyLines = rawLines.slice(startIdx, endIdx);

  // Step 3: global scrub.
  const cleanedLines = bodyLines.map((line) => scrubLine(line));

  // Collapse 3+ consecutive blank lines.
  const compacted = [];
  let blankRun = 0;
  for (const ln of cleanedLines) {
    if (!ln.trim()) {
      blankRun += 1;
      if (blankRun <= 1) compacted.push("");
    } else {
      blankRun = 0;
      compacted.push(ln);
    }
  }
  return compacted.join("\n").trim();
}

export function scrubLine(line) {
  let out = line;
  if (CONTACT_LABEL_RE.test(out)) return "";

  out = out.replace(DOB_KEYWORDS_RE, "");
  out = out.replace(EMAIL_RE, "");
  out = out.replace(SOCIAL_HANDLE_RE, "");
  out = out.replace(URL_RE, "");

  // Government IDs — order matters (longer patterns first).
  out = out.replace(CNPJ_RE, "");
  out = out.replace(CPF_RE, "");
  out = out.replace(SSN_RE, "");
  out = out.replace(SIN_RE, "");
  out = out.replace(PASSPORT_RE, "");
  out = out.replace(RG_RE, "");

  out = out.replace(PHONE_KEYWORDS_RE, "");
  out = out.replace(RAW_PHONE_RE, (match) => {
    const digits = match.replace(/\D/g, "");
    if (digits.length < 8) return match;
    // Avoid stripping calendar-year ranges like "2011 - 2015" or
    // "2011 – 2015" that are structurally similar to short phones. Real
    // phone numbers have parentheses, a country-code +, or 10+ digits.
    const hasPhoneShape = /[+()]/.test(match) || digits.length >= 10;
    if (!hasPhoneShape) {
      const yearRange = /^\s*(?:19|20)\d{2}\s*[-–—/]\s*(?:19|20)\d{2}\s*$/;
      if (yearRange.test(match)) return match;
    }
    return "";
  });

  return out.replace(/[ \t]{2,}/g, " ").replace(/\s+([,.;:])/g, "$1").trim();
}
